import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import type { SeoPage } from "@/lib/seo-engine";
import { ogFallbackForPageType } from "@/lib/seo-engine";
import { SITE_ORIGIN } from "@/lib/seo-context";
import { composePageJsonLd, type BreadcrumbCrumb } from "@/lib/seo-jsonld";
import { composePropertyPageTitle } from "@/lib/seo-h1";
import { useLocale } from "@/lib/locale-context";
import {
  DEFAULT_LOCALE,
  LOCALES,
  LOCALE_BCP47,
  withLocale,
} from "@/lib/locale";

interface SeoHeadProps {
  page?: SeoPage | null;
  /** Visual breadcrumb crumbs — used to emit BreadcrumbList JSON-LD. */
  breadcrumbs?: BreadcrumbCrumb[] | null;
  /**
   * The unprefixed (English) path of the current route — e.g. "/r/<slug>",
   * "/properties", "/contact", "/". The component prepends the active
   * locale's prefix when building canonical/hreflang URLs.
   */
  currentPath?: string;
  // Static-page fallbacks (e.g. /contact, /):
  title?: string;
  description?: string;
  noindex?: boolean;
  canonicalPath?: string;
  ogImageUrl?: string | null;
  /** Extra JSON-LD objects to inject alongside the page schemas (e.g. WebSite, Organization). */
  additionalJsonLd?: Record<string, unknown>[];
}

const DEFAULT_OG = "/opengraph.jpg";
const DEFAULT_OG_WIDTH = 1280;
const DEFAULT_OG_HEIGHT = 720;

const IS_BROWSER = typeof window !== "undefined";

function useCurrentBrand(value: string): string {
  return value.replace(/\bRiadata\b/g, "RiadIntel");
}

function absolute(origin: string, path: string): string {
  return `${origin.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
}

interface AlternateLink {
  hreflang: string;
  href: string;
}

interface HeadState {
  bcp47: string;
  title: string;
  description: string;
  canonical: string;
  noindex: boolean;
  ogType: string;
  ogLocale: string;
  ogLocaleAlternates: string[];
  ogImage: string;
  isOgFallback: boolean;
  alternates: AlternateLink[];
  jsonLd: Record<string, unknown>[];
}

// ---------------------------------------------------------------------------
// Imperative head updater — used only on the client.
//
// React 19 hoists <title>/<meta>/<link> elements rendered anywhere in the tree
// directly into <head>. With react-helmet-async@3 acting as a passthrough on
// React 19, rendering <Helmet> on the client during hydration ends up
// APPENDING a second copy of every head tag the prerender already shipped —
// breaking SEO audits that count canonical/title/description tags.
//
// To avoid duplicates while still updating the head on client-side route
// changes, we never render <Helmet> on the client. Instead we mutate the
// existing <head> in place: every tag has a stable selector so we can find
// the prerendered node (or one we created on a previous navigation) and
// update its attributes rather than appending a new node.
// ---------------------------------------------------------------------------

function upsertMeta(
  attr: "name" | "property",
  key: string,
  content: string | null | undefined,
): void {
  const selector = `meta[${attr}="${key}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (content === null || content === undefined || content === "") {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    if (existing.getAttribute("content") !== content) {
      existing.setAttribute("content", content);
    }
    return;
  }
  const el = document.createElement("meta");
  el.setAttribute(attr, key);
  el.setAttribute("content", content);
  document.head.appendChild(el);
}

function upsertCanonical(href: string): void {
  const selector = 'link[rel="canonical"]';
  const existing = document.head.querySelector<HTMLLinkElement>(selector);
  if (existing) {
    if (existing.getAttribute("href") !== href) {
      existing.setAttribute("href", href);
    }
    return;
  }
  const el = document.createElement("link");
  el.setAttribute("rel", "canonical");
  el.setAttribute("href", href);
  document.head.appendChild(el);
}

function replaceAlternates(alternates: AlternateLink[]): void {
  // Remove every existing rel="alternate" hreflang link (prerendered or
  // managed) and rebuild from the desired set. The list is small (~7 items)
  // so the cost is negligible and this guarantees no duplicates accumulate
  // across client navigations.
  const existing = document.head.querySelectorAll<HTMLLinkElement>(
    'link[rel="alternate"][hreflang]',
  );
  existing.forEach((node) => node.remove());
  for (const alt of alternates) {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", alt.hreflang);
    el.setAttribute("href", alt.href);
    document.head.appendChild(el);
  }
}

function replaceOgLocaleAlternates(values: string[]): void {
  const existing = document.head.querySelectorAll<HTMLMetaElement>(
    'meta[property="og:locale:alternate"]',
  );
  existing.forEach((node) => node.remove());
  for (const value of values) {
    const el = document.createElement("meta");
    el.setAttribute("property", "og:locale:alternate");
    el.setAttribute("content", value);
    document.head.appendChild(el);
  }
}

function replaceJsonLd(blocks: Record<string, unknown>[]): void {
  const existing = document.head.querySelectorAll<HTMLScriptElement>(
    'script[type="application/ld+json"]',
  );
  existing.forEach((node) => node.remove());
  for (const obj of blocks) {
    const el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.textContent = JSON.stringify(obj);
    document.head.appendChild(el);
  }
}

function applyHeadState(state: HeadState): void {
  if (typeof document === "undefined") return;

  document.documentElement.lang = state.bcp47;
  if (document.title !== state.title) document.title = state.title;

  upsertMeta("name", "description", state.description || null);
  upsertCanonical(state.canonical);
  upsertMeta("name", "robots", state.noindex ? "noindex,nofollow" : null);

  // Open Graph
  upsertMeta("property", "og:type", state.ogType);
  upsertMeta("property", "og:title", state.title);
  upsertMeta("property", "og:description", state.description || null);
  upsertMeta("property", "og:url", state.canonical);
  upsertMeta("property", "og:locale", state.ogLocale);
  upsertMeta("property", "og:image", state.ogImage);
  upsertMeta(
    "property",
    "og:image:width",
    state.isOgFallback ? String(DEFAULT_OG_WIDTH) : null,
  );
  upsertMeta(
    "property",
    "og:image:height",
    state.isOgFallback ? String(DEFAULT_OG_HEIGHT) : null,
  );
  upsertMeta(
    "property",
    "og:image:type",
    state.isOgFallback ? "image/jpeg" : null,
  );
  upsertMeta("property", "og:image:alt", state.title);
  upsertMeta("property", "og:site_name", "RiadIntel");

  // Twitter
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:site", "@RiadIntel");
  upsertMeta("name", "twitter:title", state.title);
  upsertMeta("name", "twitter:description", state.description || null);
  upsertMeta("name", "twitter:image", state.ogImage);
  upsertMeta("name", "twitter:image:alt", state.title);

  // Multi-instance tag groups
  replaceOgLocaleAlternates(state.ogLocaleAlternates);
  replaceAlternates(state.alternates);
  replaceJsonLd(state.jsonLd);
}

export function SeoHead({
  page,
  breadcrumbs,
  currentPath,
  title,
  description,
  noindex,
  canonicalPath,
  ogImageUrl,
  additionalJsonLd,
}: SeoHeadProps) {
  const { locale, bcp47, localizePath } = useLocale();
  // For property pages, replace the engine-generated title with our composed
  // version that surfaces the investment signal and size early in the snippet.
  const composedPropertyTitle =
    page?.pageType === "property_detail"
      ? composePropertyPageTitle(page, locale)
      : null;
  const finalTitle = useCurrentBrand(
    composedPropertyTitle ?? page?.title ?? title ?? "RiadIntel",
  );
  const finalDescription = useCurrentBrand(
    page?.metaDescription ?? description ?? "",
  );
  const supportedLocales = Array.isArray(page?.supportedLocales)
    ? page.supportedLocales.filter(
        (value): value is (typeof LOCALES)[number] =>
          typeof value === "string" && LOCALES.includes(value as (typeof LOCALES)[number]),
      )
    : [...LOCALES];
  const localeSupported = supportedLocales.includes(locale);
  const finalNoindex = (page?.noindex ?? noindex ?? false) || !localeSupported;

  // Resolve the unprefixed (English-shaped) path so we can stamp out one
  // hreflang URL per locale. Falls back to "/" when no path is provided.
  const unprefixedPath =
    currentPath ??
    canonicalPath ??
    (page ? `/r/${page.slug}` : "/");

  // Self canonical = active locale's URL. We do NOT use page.canonicalUrl
  // here because the engine emits a single English canonical which would
  // make every localised page point at the English version.
  const selfPath = withLocale(locale, unprefixedPath);
  const canonical = absolute(SITE_ORIGIN, selfPath);

  const customOg = page?.ogImageUrl ?? ogImageUrl ?? null;
  const ogPath = customOg ?? (page ? ogFallbackForPageType(page.pageType) : DEFAULT_OG);
  const isOgFallback = !customOg;
  const ogAbsolute = ogPath.startsWith("http")
    ? ogPath
    : `${SITE_ORIGIN.replace(/\/$/, "")}${ogPath}`;

  const jsonLdArr = page
    ? composePageJsonLd({
        page,
        breadcrumbs: breadcrumbs ?? null,
        siteOrigin: SITE_ORIGIN,
        currentPath: selfPath,
        localizePath,
      })
    : [];

  const allJsonLd = [...jsonLdArr, ...(additionalJsonLd ?? [])].filter(
    (obj, index, arr) =>
      obj["@type"] !== "FAQPage" ||
      index === arr.findIndex((candidate) => candidate["@type"] === "FAQPage"),
  );

  const ogType = page ? "article" : "website";

  const alternates: AlternateLink[] = finalNoindex
    ? []
    : [
        ...supportedLocales.map((loc) => ({
          hreflang: LOCALE_BCP47[loc],
          href: absolute(SITE_ORIGIN, withLocale(loc, unprefixedPath)),
        })),
        {
          hreflang: "x-default",
          href: absolute(SITE_ORIGIN, withLocale(DEFAULT_LOCALE, unprefixedPath)),
        },
      ];

  const ogLocaleAlternates = supportedLocales.filter((l) => l !== locale).map(
    (l) => LOCALE_BCP47[l],
  );

  const headState: HeadState = {
    bcp47,
    title: finalTitle,
    description: finalDescription,
    canonical,
    noindex: finalNoindex,
    ogType,
    ogLocale: LOCALE_BCP47[locale],
    ogLocaleAlternates,
    ogImage: ogAbsolute,
    isOgFallback,
    alternates,
    jsonLd: allJsonLd,
  };

  // Serialise the JSON-LD blocks for the dependency array — using the array
  // reference itself would re-run the effect on every render because we
  // build a fresh array each render.
  const jsonLdKey = JSON.stringify(allJsonLd);
  const alternatesKey = alternates.map((a) => `${a.hreflang}|${a.href}`).join(",");
  const ogAlternatesKey = ogLocaleAlternates.join(",");

  useEffect(() => {
    if (!IS_BROWSER) return;
    applyHeadState(headState);
    // Dependency list intentionally enumerates each scalar so React only
    // re-applies the head when something actually changed. The two `*Key`
    // strings stand in for the variable-length arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bcp47,
    finalTitle,
    finalDescription,
    canonical,
    finalNoindex,
    ogType,
    ogAbsolute,
    isOgFallback,
    alternatesKey,
    ogAlternatesKey,
    jsonLdKey,
  ]);

  // On the client we never render head tags through React/Helmet — see the
  // long comment above applyHeadState for why. The prerendered tags stay
  // put on first paint, and the useEffect above keeps them in sync as the
  // user navigates between routes.
  if (IS_BROWSER) return null;

  return (
    <Helmet>
      <html lang={bcp47} />
      <title>{finalTitle}</title>
      {finalDescription && <meta name="description" content={finalDescription} />}
      <link rel="canonical" href={canonical} />
      {finalNoindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Hreflang alternates — one per locale plus an x-default pointing at
          the English (default) version. Only emitted on indexable pages. */}
      {!finalNoindex &&
        supportedLocales.map((loc) => (
          <link
            key={loc}
            rel="alternate"
            hrefLang={LOCALE_BCP47[loc]}
            href={absolute(SITE_ORIGIN, withLocale(loc, unprefixedPath))}
          />
        ))}
      {!finalNoindex && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={absolute(SITE_ORIGIN, withLocale(DEFAULT_LOCALE, unprefixedPath))}
        />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={finalTitle} />
      {finalDescription && <meta property="og:description" content={finalDescription} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={LOCALE_BCP47[locale]} />
      {ogLocaleAlternates.map((value) => (
        <meta key={value} property="og:locale:alternate" content={value} />
      ))}
      <meta property="og:image" content={ogAbsolute} />
      {isOgFallback && <meta property="og:image:width" content={String(DEFAULT_OG_WIDTH)} />}
      {isOgFallback && <meta property="og:image:height" content={String(DEFAULT_OG_HEIGHT)} />}
      {isOgFallback && <meta property="og:image:type" content="image/jpeg" />}
      <meta property="og:image:alt" content={finalTitle} />
      <meta property="og:site_name" content="RiadIntel" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@RiadIntel" />
      <meta name="twitter:title" content={finalTitle} />
      {finalDescription && <meta name="twitter:description" content={finalDescription} />}
      <meta name="twitter:image" content={ogAbsolute} />
      <meta name="twitter:image:alt" content={finalTitle} />

      {allJsonLd.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
